import type { PrizeFulfilment } from '@/types'
import { delay } from '@/utils/utils'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { useGlobals } from '../composables/useGlobals'
import { type AsyncState, AsyncStateValue } from '../types'

export const usePrizesStore = defineStore('userprizes', () => {
  const prizes = ref<PrizeFulfilment[]>([])
  const state = ref<AsyncState>(AsyncStateValue.Pending)
  const error = ref<string | null>(null)

  const { prizeFulfilmentService, gameSessionService, authenticationService } =
    useGlobals()

  /**
   * Refreshes the prize list. Sets `loading` to `true` and requests a new copy
   * of the prize list from the configured prize fulfilment service. The result
   * is used to populate the reatvice `prizes` property. If the request fails
   * the `error` property is set with the reason.
   */
  const refresh = async () => {
    state.value = AsyncStateValue.Pending
    try {
      // If we previously hit an error, add a delay to allow the provider time
      // to recover before resending the request.
      if (error.value) {
        error.value = null
        await delay(2000)
      }
      prizes.value = await prizeFulfilmentService.getPrizes()
      state.value = AsyncStateValue.Resolved
    } catch (e) {
      error.value = 'Unable to load prizes'
      state.value = AsyncStateValue.Rejected
      console.error(e)
    }
  }

  const claimPhysicalPrize = async (
    fulfilmentId: string,
    address: any
  ): Promise<boolean> => {
    await prizeFulfilmentService.claimPhysicalPrize(fulfilmentId, address)
    void refresh()
    return true
  }

  const claimAltValue = async (fulfilmentId: string): Promise<boolean> => {
    await prizeFulfilmentService.claimCashAlternative(fulfilmentId)
    void refresh()
    return true
  }

  // Whenever a game session is completed, refresh the prize list
  gameSessionService.onSessionComplete(() => {
    if (authenticationService.isAuthenticated) {
      void refresh()
    }
  })

  // Watch for auth change so we can fetch the users prizes when they log in, or
  // clear up if they log out.
  authenticationService.onStateChange((state, previousState) => {
    // Only perform a refresh if we've transitioned to `authenticated` from the
    // `authenticating` state (rather than `reauthenticating`, which happens
    // during a background token refresh)
    if (state === 'authenticated' && previousState === 'authenticating') {
      void refresh()
    } else if (state === 'unauthenticated') {
      prizes.value.splice(0)
    }
  })

  // If the user is authenticated when the store is first instantiated, fetch
  // their prizes.
  if (authenticationService.isAuthenticated) {
    void refresh()
  }

  return {
    state,
    prizes,
    error,
    refresh,
    claimPhysicalPrize,
    claimAltValue,
  }
})
